import React, { useEffect, useState } from "react";
import api, { formatTL, formatDateTime } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TicketIcon, Plus, Trash } from "@phosphor-icons/react";

export default function Coupons() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [kind, setKind] = useState("percent");
  const [value, setValue] = useState("10");
  const [maxUses, setMaxUses] = useState("0");
  const [validUntil, setValidUntil] = useState("");
  const [description, setDescription] = useState("");

  const load = async () => {
    const { data } = await api.get("/coupons");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/coupons", {
        code: code.toUpperCase(),
        kind,
        value: Number(value),
        max_uses: Number(maxUses) || 0,
        valid_until: validUntil ? new Date(validUntil).toISOString() : null,
        active: true,
        description: description || null,
      });
      toast.success("Kupon oluşturuldu");
      setOpen(false);
      setCode(""); setValue("10"); setMaxUses("0"); setValidUntil(""); setDescription("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Oluşturulamadı"); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/coupons/${c.id}`, { ...c, active: !c.active });
      toast.success(`Kupon ${!c.active ? "aktifleştirildi" : "pasifleştirildi"}`);
      load();
    } catch (_) { toast.error("Güncellenemedi"); }
  };

  const del = async (c) => {
    if (!window.confirm(`${c.code} kuponunu silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/coupons/${c.id}`);
      toast.success("Silindi");
      load();
    } catch (_) { toast.error("Silinemedi"); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Promosyon</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Kupon Kodları</h1>
          <div className="text-sm text-muted-foreground mt-2">
            Ödeme anında müşteriler kupon kodunu operatöre söyleyebilir. Operatör Ödeme dialog'unda "Kupon Kodu" alanına girer.
          </div>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="coupon-new-btn"
                className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-semibold">
          <Plus size={14} /> Yeni Kupon
        </Button>
      </div>

      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kod</TableHead>
                <TableHead>Tip / Değer</TableHead>
                <TableHead>Kullanım</TableHead>
                <TableHead>Geçerlilik</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>İşlem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Henüz kupon yok</TableCell>
                </TableRow>
              )}
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`coupon-row-${c.code}`}>
                  <TableCell className="font-mono font-bold flex items-center gap-2">
                    <TicketIcon size={14} className="text-primary" />{c.code}
                  </TableCell>
                  <TableCell className="text-sm">
                    {c.kind === "percent" ? `%${c.value}` : formatTL(c.value)}
                    {c.description && <div className="text-[11px] text-muted-foreground">{c.description}</div>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : " / ∞"}
                  </TableCell>
                  <TableCell className="text-xs">
                    {c.valid_until ? formatDateTime(c.valid_until) : "Süresiz"}
                  </TableCell>
                  <TableCell>
                    <Switch checked={c.active} onCheckedChange={() => toggleActive(c)}
                            data-testid={`coupon-toggle-${c.code}`} />
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => del(c)}
                            data-testid={`coupon-delete-${c.code}`}
                            className="rounded-sm text-destructive">
                      <Trash size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Kupon Oluştur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Kod</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                     className="rounded-sm font-mono uppercase" placeholder="INDIRIM10"
                     data-testid="coupon-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tip</Label>
                <Select value={kind} onValueChange={setKind}>
                  <SelectTrigger className="rounded-sm" data-testid="coupon-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Yüzde (%)</SelectItem>
                    <SelectItem value="amount">Sabit tutar (₺)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Değer</Label>
                <Input type="number" value={value} onChange={(e) => setValue(e.target.value)}
                       className="rounded-sm font-mono" min="0" step="0.01"
                       data-testid="coupon-value-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kullanım Limiti (0 = sınırsız)</Label>
                <Input type="number" min="0" value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
                       className="rounded-sm font-mono" data-testid="coupon-max-uses" />
              </div>
              <div>
                <Label>Geçerlilik Sonu</Label>
                <Input type="datetime-local" value={validUntil}
                       onChange={(e) => setValidUntil(e.target.value)}
                       className="rounded-sm" data-testid="coupon-valid-until" />
              </div>
            </div>
            <div>
              <Label>Açıklama (opsiyonel)</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)}
                     className="rounded-sm" placeholder="Ör: Sadık müşteri indirimi"
                     data-testid="coupon-description" />
            </div>
            <Button onClick={submit} data-testid="coupon-submit-btn"
                    className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
              Oluştur
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
